import { useEffect, useRef } from 'react'
import { io, Socket } from 'socket.io-client'

const BACKEND_URL =
  typeof window !== 'undefined'
    ? (import.meta.env.VITE_BASE_BACKEND_URL || '/api')
    : ''

/**
 * Subscribes to real-time notifications for the current user via Socket.IO.
 * When the backend emits to `notification_${userId}`, the callback is invoked with the payload.
 */
export function useNotificationsSocket(
  userId: number | null | undefined,
  onNotification: (payload: unknown) => void
) {
  const socketRef = useRef<Socket | null>(null)
  const onNotificationRef = useRef(onNotification)
  onNotificationRef.current = onNotification

  useEffect(() => {
    if (typeof window === 'undefined' || !userId || !BACKEND_URL) return

    const isRelative = BACKEND_URL.startsWith('/')
    const socket = io(isRelative ? window.location.origin : BACKEND_URL, {
      path: isRelative ? `${BACKEND_URL}/socket.io/` : '/socket.io/',
      transports: ['websocket', 'polling'],
      withCredentials: true,
    })
    socketRef.current = socket

    const eventName = `notification_${userId}`
    const handler = (payload: unknown) => {
      onNotificationRef.current(payload)
    }
    socket.on(eventName, handler)

    return () => {
      socket.off(eventName, handler)
      socket.disconnect()
      socketRef.current = null
    }
  }, [userId])
}
